import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { api, errDetail, imgSrc } from "@/lib/api";
import { ConfirmDelete, ImageUploadButton, Thumb } from "@/pages/admin/shared";
import type { Tour } from "@/types";

const EMPTY: Omit<Tour, "id"> = { title_es: "", title_en: "", desc_es: "", desc_en: "", duration_es: "", duration_en: "", location: "Cancún", image: "", active: true, order: 0 };

function TourForm({ tour, onSaved }: { tour?: Tour; onSaved: () => void }) {
  const [f, setF] = useState<Omit<Tour, "id">>(() => (tour ? { ...tour } : { ...EMPTY }));
  const [saving, setSaving] = useState(false);
  const field = (k: keyof typeof f, label: string, area = false) => (
    <div className="space-y-1.5"><Label>{label}</Label>
      {area ? <Textarea rows={4} value={f[k] as string} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`tour-form-${k}`} />
        : <Input value={f[k] as string} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`tour-form-${k}`} required={k.startsWith("title")} />}
    </div>
  );
  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { id, ...body } = f as any;
      if (tour) await api.put(`/admin/tours/${tour.id}`, body);
      else await api.post("/admin/tours", body);
      toast.success("Tour guardado");
      onSaved();
    } catch (err) {
      toast.error(errDetail(err));
    } finally {
      setSaving(false);
    }
  };
  return (
    <form onSubmit={save} className="space-y-4" data-testid="tour-form">
      <div className="grid gap-4 sm:grid-cols-2">
        {field("title_es", "Título (ES)")}{field("title_en", "Título (EN)")}
        {field("duration_es", "Duración (ES)")}{field("duration_en", "Duración (EN)")}
        {field("desc_es", "Descripción (ES)", true)}{field("desc_en", "Descripción (EN)", true)}
        {field("location", "Ubicación")}
        <div className="space-y-1.5"><Label>Orden</Label><Input type="number" value={f.order} onChange={(e) => setF({ ...f, order: Number(e.target.value) })} data-testid="tour-form-order" /></div>
      </div>
      <div className="space-y-2"><Label>Imagen</Label>
        <div className="flex flex-wrap items-center gap-3">
          {f.image && <Thumb src={f.image} testId="tour-form-remove-image" onRemove={() => setF({ ...f, image: "" })} />}
          <ImageUploadButton testId="tour-form-upload" onUploaded={(u) => setF((p) => ({ ...p, image: u }))} />
        </div>
      </div>
      <label className="flex items-center gap-3 text-sm"><Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="tour-form-active" />Visible en el sitio</label>
      <button type="submit" disabled={saving} data-testid="tour-form-save-btn" className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[#0B192C] py-3 text-sm font-semibold text-white disabled:opacity-60">
        {saving && <Loader2 className="h-4 w-4 animate-spin" />}Guardar tour
      </button>
    </form>
  );
}

export default function AdminTours() {
  const { data = [], refetch } = useQuery<Tour[]>({ queryKey: ["admin-tours"], queryFn: async () => (await api.get("/admin/tours")).data });
  const [editing, setEditing] = useState<Tour | null | "new">(null);
  const remove = async (id: string) => { await api.delete(`/admin/tours/${id}`); toast.success("Tour eliminado"); refetch(); };
  return (
    <div className="max-w-5xl" data-testid="admin-tours-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <h1 className="font-display text-3xl sm:text-4xl text-[#0B192C]">Tours y Actividades</h1>
        <button onClick={() => setEditing("new")} data-testid="admin-tour-create-btn" className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37] px-5 py-2.5 text-sm font-semibold text-[#0B192C] hover:bg-[#F3E5AB]"><Plus className="h-4 w-4" />Nuevo tour</button>
      </div>
      <div className="mt-8 space-y-3">
        {data.map((tour) => (
          <div key={tour.id} data-testid={`admin-tour-${tour.id}`} className="bg-white rounded-lg border border-slate-200 p-3 flex items-center gap-4">
            <img src={imgSrc(tour.image)} alt="" className="h-16 w-24 rounded object-cover shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-[#0B192C] truncate">{tour.title_es} {!tour.active && <span className="ml-2 text-xs text-slate-400">(oculto)</span>}</p>
              <p className="text-xs text-slate-500 truncate">{tour.duration_es} · {tour.location}</p>
            </div>
            <button onClick={() => setEditing(tour)} data-testid={`admin-tour-edit-${tour.id}`} className="p-2 text-slate-600 hover:text-[#0B192C]" aria-label="Editar"><Pencil className="h-4 w-4" /></button>
            <ConfirmDelete title={`¿Eliminar ${tour.title_es}?`} onConfirm={() => remove(tour.id)}>
              <button data-testid={`admin-tour-delete-${tour.id}`} className="p-2 text-red-500 hover:text-red-700" aria-label="Eliminar"><Trash2 className="h-4 w-4" /></button>
            </ConfirmDelete>
          </div>
        ))}
      </div>
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="tour-form-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editing === "new" ? "Nuevo tour" : "Editar tour"}</DialogTitle></DialogHeader>
          {editing && <TourForm key={editing === "new" ? "new" : editing.id} tour={editing === "new" ? undefined : editing} onSaved={() => { setEditing(null); refetch(); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
